import * as moment from "moment";
import {ListItem} from "@athena-portal/journey-model";
import {lazyInject} from "@athena-portal/core";
import {ILookupService, Types as JourneyCoreTypeDefs} from "@athena-portal/plugin-journey";
import {getInteractionVehicleFuelType} from "@falcon/plugin-falcon-journey";
import {productHelper, ValuableItem} from "@falcon/services";

/**
 * Code based on LvFalconNavigator LVDataLayer mapping functionality
 */
export class LVDataLayerHelper {

    @lazyInject(JourneyCoreTypeDefs.LookupService)
    private lookupService: ILookupService;

    public mapInteractionToDataLayer(interaction: any) {

        const policyHolder: any = interaction.policyHolder ? interaction.policyHolder : null;
        const address: any = policyHolder && policyHolder.address ? policyHolder.address : null;
        const factors: any = interaction.factors ? interaction.factors : null;
        const underwritingResult: any = interaction.underwritingResult && interaction.underwritingResult.length > 0 ? interaction.underwritingResult[0] : null;
        const vehicleCount: number = interaction.vehicles ? interaction.vehicles.length : 0;
        const startDate: any = interaction.headerInfo ? moment(interaction.headerInfo.startDate).format("DD/MM/YYYY") : null;
        const realEstate: any = interaction.realEstates && interaction.realEstates[0] ? interaction.realEstates[0] : null;
        const valuableItems: any = interaction.valuableItems ? interaction.valuableItems : null;
        const paymentDay: any = interaction.paymentDay ? interaction.paymentDay : null;
        const additionalDrivers: any[] = interaction.additionalDrivers && interaction.additionalDrivers.length > 0 ? interaction.additionalDrivers : null;
        const isMotorProduct = productHelper.isMotor(interaction);
        const isHomeProduct = productHelper.isHome(interaction);

        window.LvDataLayer = {
            screenResolution: window.innerWidth + "x" + window.innerHeight,
            pageTitle: window.LvDataLayer ? window.LvDataLayer.pageTitle : null,
            partner: "lv",
            employmentStatus: policyHolder && policyHolder.employmentStatus ? this.fetchLookupValue("employmentStatus", policyHolder.employmentStatus) : null,
            maritalStatus: policyHolder && policyHolder.maritalStatus ? this.fetchLookupValue("maritalStatus", policyHolder.maritalStatus) : null,
            ukResidency: policyHolder ? policyHolder.residencyLength : null,
            ownsHome: policyHolder && policyHolder.ownsHome !== null ? (policyHolder.ownsHome ? "Yes" : "No") : null,
            claimsIndicated: policyHolder && policyHolder.hasClaims !== null ? (policyHolder.hasClaims ? "Yes" : "No") : null,
            convictionsIndicated: policyHolder && policyHolder.hasConvictions !== null ? (policyHolder.hasConvictions ? "Yes" : "No") : null,
            yearOfBirth: policyHolder && policyHolder.dateOfBirth ? this.extractYearFromDate(policyHolder.dateOfBirth) : null,
            emailAddress: policyHolder ? policyHolder.email : null,
            postCode: address ? address.outwardPostcode : null,
            numberOfDrivers: isMotorProduct ? this.getNumberOfDrivers(interaction) : null,
            totalPricePerMonth: factors ? factors.premium : null,
            quoteReference: interaction._id,
            marketingPreferenceEmail: policyHolder ? (policyHolder.marketingPreferencesEmail ? "Y" : "N") : null,
            marketingPreferencePhone: policyHolder ? (policyHolder.marketingPreferencesPhone ? "Y" : "N") : null,
            marketingPreferencePost: policyHolder ? (policyHolder.marketingPreferencesPost ? "Y" : "N") : null,
            marketingPreferenceSms: policyHolder ? (policyHolder.marketingPreferencesSMS ? "Y" : "N") : null,
            policyReference: interaction.agreementNumber,
            failedUwRule: underwritingResult ? underwritingResult.objectName : null,
            vehicles: this.getVehicleData(interaction),
            numberOfCars: vehicleCount,
            multipleCars: isMotorProduct ? (vehicleCount > 1 ? "Y" : "N") : null,
            documentMethod: policyHolder && policyHolder.sendDocumentsByPost ? "Post" : "Email",
            startingProduct: this.getDataLayerProduct(),
            coverStartDate: startDate,
            liveAlone: realEstate ? (realEstate.hasNoOtherResidents ? "Y" : "N") : null,
            propertyType: realEstate && this.getTypeOrKindOfProperty(realEstate, true),
            kindOfProperty: realEstate && this.getTypeOrKindOfProperty(realEstate, false),
            numberOfFamilyMembers: realEstate && realEstate.adultFamilyMemberCount,
            numberOfHousemates: realEstate && realEstate.housemateCount,
            gotKids: realEstate ? (realEstate.childrenAtProperty ? "Y" : "N") : null,
            typeOfTenancy: realEstate && realEstate.furnishingType && this.fetchLookupValue("furnishingType", realEstate.furnishingType),
            numberOfBedrooms: realEstate && realEstate.bedroomCount,
            numberOfBathrooms: realEstate && realEstate.bathroomCount,
            includedItemsWorthMoreThan2K: valuableItems ? (this.calculateValuablesTotalValue(valuableItems) > 2000 ? "Y" : "N") : null,

            licenceHandy: policyHolder ? policyHolder.drivingLicenceHandy : null,
            title: policyHolder ? policyHolder.title : null,
            firstName: policyHolder ? policyHolder.firstName : null,
            lastName: policyHolder ? policyHolder.lastName : null,
            dln: policyHolder && policyHolder.licence != null ? policyHolder.licence.number : null,
            licenceType: policyHolder && policyHolder.licence && policyHolder.licence.type ? this.fetchLookupValue("licenceType", policyHolder.licence.type.toString()) : null,
            licenceHeldYears: policyHolder && policyHolder.drivingExperience ? this.fetchLookupValue("drivingExperienceOpts", policyHolder.drivingExperience.toString()) : null,
            occupation: policyHolder && policyHolder.occupation ? this.fetchLookupValue("wolfOccupation", policyHolder.occupation) : null,
            occupationIndustry: policyHolder && policyHolder.occupationIndustry ? this.fetchLookupValue("wolfOccupationIndustry", policyHolder.occupationIndustry) : null,
            hasSecondaryOccupation: policyHolder ? (policyHolder.hasSecondaryOccupation ? "Y" : "N") : null,
            secondaryOccupation: policyHolder && policyHolder.secondaryOccupation ? this.fetchLookupValue("wolfOccupation", policyHolder.secondaryOccupation) : null,
            numberOfClaims: policyHolder ? policyHolder.numberOfClaims : null,
            ukResidentOver3Years: policyHolder ? (policyHolder.residentOfUKMoreThanThreeYears ? "Y" : "N") : null,
            residencyTerm: policyHolder && policyHolder.residencyTerm ? this.fetchLookupValue("residencyTerms", policyHolder.residencyTerm.toString()) : null,
            nonMotoringConvictions: policyHolder && policyHolder.hasUnspentCriminalConvictions != null ? (policyHolder.hasUnspentCriminalConvictions ? "Y" : "N") : null,
            ncdYears: policyHolder && policyHolder.ncd ? policyHolder.ncd.yearCount : null,
            ncdEarningType: policyHolder && policyHolder.ncd && policyHolder.ncd.earningType ? this.fetchLookupValue("ncdEarnType", policyHolder.ncd.earningType.toString()) : null,
            protectedNcd: policyHolder && policyHolder.ncd ? (policyHolder.ncd.doNotDisclose ? "Y" : "N") : null,
            carsInHousehold: policyHolder ? policyHolder.carsInHousehold : null,
            paymentOption: factors ? factors.premiumType : null,
            coverType: "COMPREHENSIVE",
            automaticRenewal: policyHolder ? policyHolder.automaticRenewal : null,
            aggregatorName: this.populateAggregatorInfo(interaction),
            paymentDay: paymentDay ? paymentDay.day : null,
            addDriver: additionalDrivers ? "Y" : "N",

            externalWallMaterial: realEstate && realEstate.externalWallMaterial ? this.fetchLookupValue("wallMaterial", realEstate.externalWallMaterial) : null,
            roofMaterial: realEstate && realEstate.roofMaterial ? this.fetchLookupValue("roofMaterial", realEstate.roofMaterial.toString()) : null,
            yearBuilt: realEstate && realEstate.yearBuilt ? realEstate.yearBuilt : null,
            propertyListing: realEstate && realEstate.propertyListing ? this.fetchLookupValue("propertyListing", realEstate.propertyListing) : null,
            isMainHome: realEstate && realEstate.isMainHome != null ? (realEstate.isMainHome ? "Y" : "N") : null,
            residentsType: realEstate && realEstate.residentsType ? this.fetchLookupValue("residencyUsage", realEstate.residentsType) : null,
            numberOfChildren: realEstate && realEstate.numberOfChildren ? realEstate.numberOfChildren : null,
            isLeftEmpty: realEstate && realEstate.isLeftEmpty != null ? (realEstate.isLeftEmpty ? "Y" : "N") : null,
            ownershipType: realEstate && realEstate.ownershipType ? this.fetchLookupValue("propertyOwnershipType", realEstate.ownershipType) : null,
            yearsAtProperty: policyHolder ? policyHolder.yearsAtProperty : null,
            homePolicyHolderTitle: policyHolder && policyHolder.title ? this.fetchLookupValue("homePolicyHolderTitle", policyHolder.title) : null,
            hasJointPolicyHolder: policyHolder && policyHolder.hasJointPolicyHolder != null ? (policyHolder.hasJointPolicyHolder ? "Y" : "N") : null,
            monthlyPaymentAvailable: interaction.paymentDetails ?  (interaction.paymentDetails.instalmentPlan && interaction.paymentDetails.instalmentPlan.instalmentPlanParameters ? "Y" : "N") : null,
            documentsByEmail: policyHolder && policyHolder.sendDocumentsByPost ? "N" : "Y",
            annualPremium: factors ? factors.annualPremium : null,
            homeAnnualPremium: isHomeProduct && factors ? factors.annualPremium : null,
            homeCoverLevel: isHomeProduct ? interaction.coverLevel : null,
            homeCoverTier: isHomeProduct ? interaction.coverTier : null,
            homeFixedAddonsBuildingAccidentalDamage: isHomeProduct && interaction.annualHomeFixedAddons ? (interaction.annualHomeFixedAddons.buildingAccidentalDamage ? "Y" : "N") : null,
            homeFixedAddonsContentsAccidentalDamage: isHomeProduct && interaction.annualHomeFixedAddons ? (interaction.annualHomeFixedAddons.contentsAccidentalDamage ? "Y" : "N") : null,
            homeFixedAddonsHomeEmergency: isHomeProduct && interaction.annualHomeFixedAddons ? (interaction.annualHomeFixedAddons.homeEmergency ? "Y" : "N") : null,
            homeFixedAddonsHomeLegalExpenses: isHomeProduct && interaction.annualHomeFixedAddons ? (interaction.annualHomeFixedAddons.homeLegalExpenses ? "Y" : "N") : null,
            coverTier: interaction.coverTier
        };
        return null;
    }

    private getVehicleData(interaction: any): any[] {
        const vehicleInfo: any[] = [];

        if (interaction.vehicles) {
            const optionalExtras: any = interaction.optionalExtras;

            interaction.vehicles.forEach((vehicle: any) => {
                const legalProtection: any = optionalExtras ? (optionalExtras.motorLegalExpenses ? "Y" : "N") : null;
                const courtesyCar: any = optionalExtras ? (optionalExtras.courtesyCar ? "Y" : "N") : null;
                const courtesyCarTotalLoss: any = this.setValueOrNull(
                    optionalExtras,
                    () => this.getYN(optionalExtras?.courtesyCarTotalLoss)
                );

                const car: any = {
                    yearOfManufacture: vehicle.year,
                    fuelType: vehicle.fuelType ? this.fetchLookupValue("vehicleFuelType", getInteractionVehicleFuelType(vehicle.fuelType, vehicle.fuelTypeFull)) : null,
                    fuelTypeFull: vehicle.fuelTypeFull ? vehicle.fuelTypeFull : null,
                    make: vehicle.make,
                    model: vehicle.model,
                    voluntaryExcess: this.getVoluntaryExcess(vehicle),
                    courtesyCar,
                    courtesyCarCost: courtesyCar ? optionalExtras.courtesyCarCost : null,
                    courtesyCarTotalLoss,
                    courtesyCarTotalLossCost: this.setValueOrNull(courtesyCarTotalLoss, () => optionalExtras?.courtesyCarTotalLossCost),
                    motorLegalExpensesCost: legalProtection ? optionalExtras.motorLegalExpensesCost : null,
                    motorLegalExpenses: legalProtection,
                    courtesyCarAmount: courtesyCar ? optionalExtras.courtesyCarCost : null,
                    totalPricePerMonth: vehicle.ratingOutput ? vehicle.ratingOutput.grossPremium : null,
                    pricePerMonth: vehicle.ratingOutput ? vehicle.ratingOutput.roundedPremium : null,
                    regNumberProvided: vehicle.manuallyChosen != null ? (vehicle.manuallyChosen ? "N" : "Y") : null,
                    vrn: vehicle.registrationNumber,
                    transmission: vehicle.transmission ? this.fetchLookupValue("vehicleTransmission", vehicle.transmission) : null,
                    modifications: vehicle.hasModifications != null ? (vehicle.hasModifications ? "Y" : "N") : null,
                    trackerInstalled: vehicle.trackerQuestionRequired ? (vehicle.trackerInstalled ? "Y" : "N") : null,
                    mileage: vehicle.mileage ? this.fetchLookupValue("mileage", vehicle.mileage.toString()) : null,
                    carUsage: vehicle.classOfUse ? this.fetchLookupValue("carUsage", vehicle.classOfUse) : null,
                    breakdownCover: optionalExtras ? (optionalExtras.breakdownCover ? "Y" : "N") : null,
                    breakdownCoverType: optionalExtras && optionalExtras.breakdownCoverLevel ? optionalExtras.breakdownCoverLevel : null,
                    familyPackage: optionalExtras ? (optionalExtras.familyPackage ? "Y" : "N") : null
                };

                vehicleInfo.push(car);
            })
        }

        return vehicleInfo;
    }

    private getYN(value: any): "Y" | "N" {
        return value ? "Y" : "N";
    }

    private setValueOrNull(condition: any, valueAccessor: () => any) {
        return condition != null ? valueAccessor() : null;
    }

    private populateAggregatorInfo(interaction: any): string | null {
        if (interaction.source) {
            let aggregatorName: string;
            switch (interaction.source) {
                case 'ctm':
                    aggregatorName = 'CTM';
                    break;
                case 'cnf':
                    aggregatorName = 'CONF';
                    break;
                case 'msm':
                    aggregatorName = 'MSM';
                    break;
                case 'gc':
                    aggregatorName = 'GOCO';
                    break;
                default:
                    return null;
            }

            let line: string = `LVCar/Aggregator/${aggregatorName}`;
            if (interaction.product) {
                line += '/' + interaction.product;
            }
            return line;
        }

        return null;
    }

    private fetchLookupValue(fieldId: string, id: string): string {
        let value: string = "";

        this.lookupService.fetchLookupValues(fieldId, "en-UK").subscribe((lookupValues: ListItem[]) => {
            value = lookupValues?.find(entry => entry?.id === id)?.value ?? "";
        });

        return value;
    }

    private extractYearFromDate(date: string): number {
        return moment(date, moment.ISO_8601).year();
    }

    private getNumberOfDrivers(interaction: any): number | null {
        if (interaction.policyHolder) {
            if (interaction.additionalDrivers) {
                return interaction.additionalDrivers.length + 1;
            } else {
                return 1;
            }
        }

        return null;
    }

    private getTypeOrKindOfProperty(realEstate: any, type: boolean): any {
        const estateTypeSubType = realEstate.realEstateTypeSubType ? this.fetchLookupValue("realEstateTypeSubType", realEstate.realEstateTypeSubType) : null;
        if (estateTypeSubType) {
            const lastWord = estateTypeSubType.split(" ").pop();
            return lastWord ? (type ? lastWord.charAt(0).toUpperCase() + lastWord.substring(1, lastWord.length) : estateTypeSubType.replace(lastWord, "").trim()) : null;
        }
        return null;
    }


    private getVoluntaryExcess(vehicle: any): number | null {
        if (vehicle.excesses) {
            return vehicle.excesses.voluntaryExcess;
        } else if (vehicle.annualVehicleExcess) {
            return vehicle.annualVehicleExcess.voluntaryExcess;
        }

        return null;
    }

    private getDataLayerProduct(): string | null {
        return "Motor Annual";
    }

    private calculateValuablesTotalValue(valuableItems: ValuableItem[]): number {
        return valuableItems.map((item: ValuableItem) => item.value).reduce((total, next) => Number(total) + Number(next), 0);
    }
}



// WEBPACK FOOTER //
// ./src/utils/LVDataLayerHelper.tsx